/* Native, dependency-free carousel. Focus and manual navigation stop rotation. */
if (!customElements.get('hp-equipment')) {
  class HeptagonEquipment extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.slides = [...this.querySelectorAll('[data-equipment-slide]')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.hovered = false;
      this.visible = true;
      this.togglePauseTarget = null;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || this.dataset.autoplay !== 'true';
      this.rotation = this.querySelector('[data-rotation]');
      this.track = this.querySelector('[data-equipment-track]');
      this.querySelector('[data-equipment-controls]').hidden = false;

      this.rotation.addEventListener('pointerdown', () => {
        // Preserve the intended click action before focusin pauses rotation.
        this.togglePauseTarget = !this.paused;
      }, { signal });
      this.rotation.addEventListener('pointercancel', () => { this.togglePauseTarget = null; }, { signal });
      this.rotation.addEventListener('click', () => {
        this.setPaused(this.togglePauseTarget ?? !this.paused);
        this.togglePauseTarget = null;
      }, { signal });
      this.querySelector('[data-previous]').addEventListener('click', () => this.navigate(-1), { signal });
      this.querySelector('[data-next]').addEventListener('click', () => this.navigate(1), { signal });
      this.addEventListener('focusin', (event) => {
        if (!this.contains(event.relatedTarget)) this.setPaused(true);
      }, { signal });
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, { signal });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', (event) => {
        if (event.matches) this.setPaused(true);
      }, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.schedule();
        }, { threshold: 0.2 });
        this.observer.observe(this);
      }
      this.show(0);
      this.setPaused(this.paused);
    }

    navigate(delta) {
      this.setPaused(true);
      this.show(this.index + delta);
    }

    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      // Prime the next photo without downloading every slide on initial load.
      const nextImage = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
      if (nextImage) nextImage.loading = 'eager';
    }

    setPaused(paused) {
      this.paused = paused;
      const label = paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      this.rotation.setAttribute('aria-label', label);
      this.rotation.title = label;
      this.rotation.querySelector('[data-play-icon]').hidden = !paused;
      this.rotation.querySelector('[data-pause-icon]').hidden = paused;
      this.schedule();
    }

    schedule() {
      window.clearTimeout(this.timer);
      const rotating = !this.paused && !this.hovered && this.visible && !document.hidden;
      this.track.setAttribute('aria-live', rotating ? 'off' : 'polite');
      if (rotating) {
        this.timer = window.setTimeout(() => {
          this.show(this.index + 1);
          this.schedule();
        }, 6500);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.observer?.disconnect();
      this.abort?.abort();
    }
  }
  customElements.define('hp-equipment', HeptagonEquipment);
}
